import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { IoEye } from "react-icons/io5";
import { FiEdit2, FiFilter, FiRotateCcw, FiSearch } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";

function ManageRoles() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [loadingRoleId, setLoadingRoleId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });

  const buildParams = useCallback(() => {
    const params = {};
    if (nameFilter.trim()) params.name = nameFilter.trim();
    if (statusFilter !== "") params.status = statusFilter;
    return params;
  }, [nameFilter, statusFilter]);

  const fetchRoles = useCallback(async (nextOffset = offset, overrideParams = appliedFilters) => {
    try {
      setIsLoading(true);
      const response = await Apis.getRoles(nextOffset, limit, overrideParams);
      const payload = response.data.data;
      setRoles(payload.roles || []);
      setPagination(payload.pagination || {
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: limit,
      });
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to fetch roles.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, offset, limit]);

  useEffect(() => {
    fetchRoles(offset);
  }, [offset, fetchRoles]);

  const handleApplyFilter = () => {
    const nextFilters = buildParams();
    setAppliedFilters(nextFilters);
    setOffset(0);
    fetchRoles(0, nextFilters);
  };

  const handleResetFilter = () => {
    setNameFilter("");
    setStatusFilter("");
    setAppliedFilters({});
    setOffset(0);
    fetchRoles(0, {});
  };

  const handleToggleStatus = async (role) => {
    try {
      setLoadingRoleId(role.id);
      await Apis.toggleRoleStatus(role.id);
      setRoles((prevRoles) =>
        prevRoles.map((item) =>
          item.id === role.id ? { ...item, status: !item.status } : item,
        ),
      );
      Swal.fire("Success", "Role status updated successfully.", "success");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to update role status.", "error");
    } finally {
      setLoadingRoleId(null);
    }
  };

  const handleDelete = (role) => {
    Swal.fire({
      title: "Delete role?",
      text: "This role will be soft deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await Apis.deleteRole(role.id);
        Swal.fire("Deleted", "Role deleted successfully.", "success");
        fetchRoles(offset);
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to delete role.", "error");
      }
    });
  };

  if (isLoading) {
    return <Loader message="Loading roles..." />;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-2xl font-bold">Manage Roles</h2>
        <button
          className="btn btn-filter text-white"
          onClick={() => navigate("/roles/create")}
        >
          Create New Role
        </button>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by role name"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="">Select Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleApplyFilter}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button className="btn btn-reset-filter filter-btn" onClick={handleResetFilter}>
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Name</th>
                <th className="table-th">Description</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8">
                    No roles found.
                  </td>
                </tr>
              ) : (
                roles.map((role, index) => (
                  <tr
                    key={role.id}
                    className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"} hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td font-medium">{role.name}</td>
                    <td className="table-td">{role.description || "-"}</td>
                    <td className="table-td">
                      <div
                        className={`status-toggle ${role.status ? "active" : ""} ${loadingRoleId === role.id ? "loading" : ""}`}
                        onClick={() => loadingRoleId !== role.id && handleToggleStatus(role)}
                      >
                        <div className="status-toggle-handle"></div>
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="btn btn-view text-white px-3"
                          onClick={() => navigate(`/roles/view/${role.id}`, { state: { role, mode: "view" } })}
                          title="View"
                        >
                          <IoEye />
                        </button>
                        <button
                          className="btn btn-edit text-white px-3"
                          onClick={() => navigate(`/roles/edit/${role.id}`, { state: { role, mode: "edit" } })}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="btn btn-delete text-white px-3"
                          onClick={() => handleDelete(role)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-5">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={pagination.totalRecords}
            onPageChange={setOffset}
          />
        </div>
      )}
    </div>
  );
}

export default ManageRoles;
